import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { notificationsApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Mail,
  Inbox,
  Archive,
  Trash2,
  Shield,
  Bell,
  Trophy,
  CheckCheck,
  RefreshCw,
  ChevronLeft,
  MoreHorizontal,
  Search,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { useI18n } from '../contexts/I18nContext';

const FOLDERS = [
  { id: 'inbox', icon: Inbox },
  { id: 'system', icon: Mail },
  { id: 'moderation', icon: Shield },
  { id: 'archive', icon: Archive },
];

const getNotificationIcon = (type) => {
  switch (type) {
    case 'system': return <Mail className="h-4 w-4 text-blue-500" />;
    case 'achievement': return <Trophy className="h-4 w-4 text-amber-500" />;
    case 'moderation': return <Shield className="h-4 w-4 text-purple-500" />;
    default: return <Bell className="h-4 w-4 text-muted-foreground" />;
  }
};

const formatDate = (dateString, t) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('mailRoom.time.justNow');
  if (diffMins < 60) return t('mailRoom.time.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('mailRoom.time.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('mailRoom.time.daysAgo', { count: diffDays });
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatFullDate = (dateString) => {
  return new Date(dateString).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function MailRoom() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState(searchParams.get('folder') || 'inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationsApi.getNotifications({ folder, limit: 50 });
      setNotifications(response.data.notifications || []);
      
      // Update unread count
      const countResponse = await notificationsApi.getUnreadCount();
      setUnreadCounts({ inbox: countResponse.data.unread_count || 0 });
    } catch (err) {
      console.error('Error fetching notifications:', err);
      toast.error(t('mailRoom.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [folder]);

  // Update URL when folder changes
  useEffect(() => {
    setSearchParams({ folder });
  }, [folder, setSearchParams]);

  // Handle notification click
  const handleSelectNotification = async (notification) => {
    setSelectedNotification(notification);
    
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await notificationsApi.markAsRead(notification.notification_id);
        setNotifications(prev =>
          prev.map(n =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true }
              : n
          )
        );
        setUnreadCounts(prev => ({ ...prev, inbox: Math.max(0, (prev.inbox || 0) - 1) }));
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCounts({ inbox: 0 });
      toast.success(t('mailRoom.allMarkedRead'));
    } catch (err) {
      toast.error(t('mailRoom.markReadFailed'));
    }
  };

  // Archive notification
  const handleArchive = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId); // Using archive endpoint
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      if (selectedNotification?.notification_id === notificationId) {
        setSelectedNotification(null);
      }
      toast.success(t('mailRoom.messageArchived'));
    } catch (err) {
      toast.error(t('mailRoom.archiveFailed'));
    }
  };

  // Delete notification
  const handleDelete = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId); // Would use delete endpoint
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      if (selectedNotification?.notification_id === notificationId) {
        setSelectedNotification(null);
      }
      toast.success(t('mailRoom.messageDeleted'));
    } catch (err) {
      toast.error(t('mailRoom.deleteFailed'));
    }
  };

  // Filter notifications by search
  const filteredNotifications = notifications.filter(n => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      n.subject?.toLowerCase().includes(query) ||
      n.body_preview?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="mailroom-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            {t('mailRoom.headerTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('mailRoom.headerSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => toast.info(t('mailRoom.composeComingSoon'))}
          >
            <Mail className="h-4 w-4 mr-2" />
            {t('mailRoom.compose')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={unreadCounts.inbox === 0}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            {t('mailRoom.markAllRead')}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchNotifications}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Card className="flex-1 overflow-hidden">
        <div className="flex h-full">
          {/* Sidebar - Folders */}
          <div className="w-48 border-r bg-muted/30 p-3 flex-shrink-0">
            <div className="space-y-1">
              {FOLDERS.map(f => {
                const Icon = f.icon;
                const isActive = folder === f.id;
                const count = f.id === 'inbox' ? unreadCounts.inbox : 0;
                
                return (
                  <button
                    key={f.id}
                    onClick={() => setFolder(f.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`folder-${f.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {f.name}
                    </span>
                    {count > 0 && (
                      <Badge variant={isActive ? "secondary" : "default"} className="text-xs">
                        {count}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message List */}
          <div className="w-80 border-r flex flex-col flex-shrink-0">
            {/* Search */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('mailRoom.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-3 space-y-3">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{t('mailRoom.noMessagesFolder')}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredNotifications.map(notification => (
                    <button
                      key={notification.notification_id}
                      onClick={() => handleSelectNotification(notification)}
                      className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${
                        selectedNotification?.notification_id === notification.notification_id
                          ? 'bg-muted'
                          : ''
                      } ${!notification.is_read ? 'bg-primary/5' : ''}`}
                      data-testid={`notification-${notification.notification_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {!notification.is_read && (
                            <span className="absolute w-2 h-2 bg-primary rounded-full -ml-1" />
                          )}
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${!notification.is_read ? 'font-semibold' : ''}`}>
                              {notification.subject}
                            </p>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {formatDate(notification.created_at, t)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {notification.body_preview}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Reading Pane */}
          <div className="flex-1 flex flex-col">
            {selectedNotification ? (
              <>
                {/* Message Header */}
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-muted">
                        {getNotificationIcon(selectedNotification.type)}
                      </div>
                      <div>
                        <h2 className="font-semibold">{selectedNotification.subject}</h2>
                        <p className="text-sm text-muted-foreground">
                          {formatFullDate(selectedNotification.created_at)}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleArchive(selectedNotification.notification_id)}>
                          <Archive className="h-4 w-4 mr-2" />
                          {t('mailRoom.actions.archive')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(selectedNotification.notification_id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('mailRoom.actions.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Message Body */}
                <ScrollArea className="flex-1 p-6">
                  <div 
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedNotification.body }}
                  />
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Mail className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>{t('mailRoom.selectMessage')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
